import {
  Box,
  Button,
  Collapse,
  Flex,
  FormLabel,
  Text,
  VStack
} from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { format, isBefore, sub } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { fixedAssets } from 'src/api/models/fixedAssets'
import { request_fixedAssets } from 'src/api/models/request_fixedAssets'
import { response_fixedAsset } from 'src/api/models/response_fixedAsset'
import { schemaForType } from 'src/common/schemeForType'
import { FormNumberInput } from 'src/components/common/Forms/FormNumberInput'
import { FormPriceAmountInput } from 'src/components/common/Forms/FormPriceAmountInput'
import { FormSelect } from 'src/components/common/Forms/FormSelect'
import { FormTextInput } from 'src/components/common/Forms/FormTextInput'
import { FormYearSelect } from 'src/components/common/Forms/FormYearSelect'
import { QuestionTooltip } from 'src/components/common/QuestionToolTip'
import { useAccountItems } from 'src/hooks/useAccountItems'
import { useFixedAsset } from 'src/hooks/useFixedAsset'
import { useFixedAssets } from 'src/hooks/useFixedAssets'
import { useProcessingYear } from 'src/hooks/useProcessingYear'
import { FormSection } from 'src/pages/tax_tasks/opening/components/TaxTaskTodoList/TaxTaskTodoModal/FormSection'
import { SaveButtons } from 'src/pages/tax_tasks/opening/components/TaxTaskTodoList/TaxTaskTodoModal/SaveButtons'
import { SubTodoProps } from 'src/pages/tax_tasks/opening/components/TaxTaskTodoList/TaxTaskTodoModal/TodoOne/SubTodoOne'
import * as z from 'zod'
import { useConfirmServerUpdateDialog } from '../FixedAssetsList/hooks/useConfirmServerUpdateDialog'
import { DataDisplay } from './DataDisplay'

const accountItemNameToOrderMap = (name: string) => {
  switch (name) {
    case '備品':
      return 1
    case 'ソフトウェア':
      return 2
    case '開業費':
      return 3
    case '機械装置':
      return 4
    case '車両運搬具':
      return 5
    case '一括償却資産':
      return 6
    default:
      return 7
  }
}

const {
  STRAIGHT_LINE,
  DECLINING_BALANCE,
  PETTY_SUM,
  LUMP_SUM,
  STRAIGHT_LINE_METHOD,
  VOLUNTARY
} = response_fixedAsset.depreciation_method

export const getDepreciationMethodName = (
  method?: response_fixedAsset.depreciation_method | null
) => {
  switch (method) {
    case STRAIGHT_LINE:
      return '定額法'
    case DECLINING_BALANCE:
      return '定率法'
    case PETTY_SUM:
      return '少額償却'
    case LUMP_SUM:
      return '一括償却'
    case VOLUNTARY:
      return '任意償却'
    case STRAIGHT_LINE_METHOD:
      return '均等償却'

    default:
      return '定額法'
  }
}

export const SubTodoTwo: React.VFC<
  SubTodoProps & {
    fixedAsset: response_fixedAsset | undefined
    setCurrentAssetId?: (id: fixedAssets['id']) => void
    title: string
    id: string
    index: number
    isOnCreateModal: boolean
  }
> = ({
  done,
  isEditing,
  setIsEditing,
  id,
  index,
  fixedAsset,
  isOnCreateModal,
  setCurrentAssetId
}) => {
  const { postFixedAsset, mutateFixedAssets } = useFixedAssets()
  const { patchFixedAssetBasicInfo } = useFixedAsset(fixedAsset?.id)
  const { accountItems } = useAccountItems(undefined, 'fixed_asset')
  const [renderConfirmDialog, { onOpen: onOpenConfirmDialog }] =
    useConfirmServerUpdateDialog()
  const { processing_year } = useProcessingYear()

  // 編集中の固定資産が前年度以前に償却を持つか
  const hasPreviousDepreciation =
    fixedAsset && done && fixedAsset.has_pre_year_depreciation

  const schema = useMemo(
    () =>
      schemaForType<request_fixedAssets>()(
        z.object({
          name: z
            .string()
            .min(1, '資産の名前を入力してください')
            .max(16, '資産の名前は、全角16文字以内で入力してください'),
          acquisition_date: z.preprocess(
            (value) => {
              if (typeof value !== 'string') return value
              return new Date(value)
            },
            z
              .date({
                required_error: '取得日を入力してください',
                invalid_type_error: '正しい日付を入力してください'
              })
              .min(new Date('1900/1/1'), {
                message:
                  '取得日は、1900/1/1 から 2099/12/31の間の日付を入力してください'
              })
              .max(new Date('2099/12/31'), {
                message:
                  '取得日は、1900/1/1 から 2099/12/31の間の日付を入力してください'
              })
              .refine(
                (value) =>
                  !fixedAsset ||
                  (fixedAsset.service_date &&
                    isBefore(
                      sub(value, { days: 1 }),
                      new Date(fixedAsset.service_date)
                    )),
                '取得日は使用開始日以前の日付を入力してください。'
              )
              .transform((v) => {
                return format(new Date(v), 'yyyy-MM-dd')
              })
          ),
          acquisition_amount: z
            .number()
            .min(1, '金額は、1円以上の金額を入力してください')
            .max(999999999, '金額は、999,999,999円以下の金額を入力してください')
            .refine((value) => {
              if (
                fixedAsset &&
                fixedAsset.depreciation_method === PETTY_SUM &&
                (value < 100000 || value >= 300000)
              ) {
                return false
              }
              return true
            }, '少額償却を選ぶ場合、金額は10万円以上30万円未満で入力してください。')
            .refine((value) => {
              if (
                fixedAsset &&
                fixedAsset.depreciation_method === LUMP_SUM &&
                (value < 100000 || value >= 200000)
              ) {
                return false
              }
              return true
            }, '一括償却を選ぶ場合、金額は10万円以上20万円未満で入力してください。'),
          quantity: z
            .number({
              required_error: '数量は、1以上の数量を入力してください',
              invalid_type_error: '数量は、1以上の数量を入力してください'
            })
            .min(1, '数量は、1以上の数量を入力してください')
            .max(999999999, '数量は、999,999,999以下の数量を入力してください')
            .refine((value) => {
              if (Number.isInteger(value)) return true
              if (Number.isNaN(Number(value))) return false
              const strArr = value.toString().split('.')
              const floatStr = strArr[1]
              return floatStr && floatStr.length < 11
            }, '数量は、小数点以下10桁までで入力してください'),
          account_item_id: z.preprocess(
            (value) => (value ? Number(value) : undefined),
            z
              .number({ required_error: 'カテゴリを選択してください' })
              .min(1, 'カテゴリを選択してください')
              .transform((value) => Number(value))
          )
        })
      ),
    [fixedAsset]
  )

  type FormInputs = z.infer<typeof schema>

  const methods = useForm<FormInputs>({
    defaultValues: {
      acquisition_amount: 0,
      quantity: 1
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema)
  })
  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, dirtyFields }
  } = methods

  const confirmServerUpdateBeforeSubmit = () => {
    if (
      // (fixedAsset.beginning_amount > 0 || fixedAsset.revised_acquisition_amount > 0 ) &&
      fixedAsset &&
      fixedAsset.revised_acquisition_amount !== null &&
      fixedAsset.revised_acquisition_amount > 0 &&
      dirtyFields.acquisition_date &&
      new Date(watch('acquisition_date')).getFullYear() ===
        processing_year?.processing_year
    ) {
      onOpenConfirmDialog({
        header: '取得日を今年度に変更しようとしています',
        body: `取得日が今年度の状態では
        ${
          fixedAsset?.revised_acquisition_amount &&
          fixedAsset?.revised_acquisition_amount
            ? '期首残高・改定取得価額'
            : fixedAsset?.revised_acquisition_amount
            ? '改定取得価額'
            : fixedAsset?.revised_acquisition_amount && '期首残高'
        }
        を入力できないため、入力されている
        ${
          fixedAsset?.revised_acquisition_amount &&
          fixedAsset?.revised_acquisition_amount
            ? '期首残高・改定取得価額'
            : fixedAsset?.revised_acquisition_amount
            ? '改定取得価額'
            : fixedAsset?.revised_acquisition_amount && '期首残高'
        }
        がゼロになりますがよろしいですか？`
      })
      return false
    } else if (
      // 開業費を選択する際、償却方法が「任意償却」か「均等償却」以外だった場合は、サーバーで均等償却にsetし直す
      Number(watch('account_item_id')) ===
        accountItems?.find((item) => item.name === '開業費')?.id &&
      fixedAsset &&
      fixedAsset?.depreciation_method !== VOLUNTARY &&
      fixedAsset?.depreciation_method !== STRAIGHT_LINE_METHOD
    ) {
      onOpenConfirmDialog({
        header: 'カテゴリを開業費に変更しようとしています',
        body: `開業費を選択した場合、${getDepreciationMethodName(
          fixedAsset.depreciation_method
        )}は選択できないため、均等償却へと変更されます。よろしいですか？`
      })
      return false
    } else if (
      // 償却方法を「任意償却」or「均等償却」にして、カテゴリを「開業費」から変えた
      fixedAsset &&
      (fixedAsset.depreciation_method === VOLUNTARY ||
        fixedAsset.depreciation_method === STRAIGHT_LINE_METHOD) &&
      fixedAsset.account_item.name === '開業費' &&
      Number(watch('account_item_id')) !==
        accountItems?.find((item) => item.name === '開業費')?.id
    ) {
      const categoryName = accountItems?.find(
        (item) => Number(item.id) === Number(watch('account_item_id'))
      )?.name
      onOpenConfirmDialog({
        header: `カテゴリを${categoryName}に変更しようとしています`,
        body: (
          <>
            {categoryName}を選択した場合、
            <br />
            {getDepreciationMethodName(fixedAsset.depreciation_method)}
            は選択できないため、定額法へと変更され、耐用年数は3年に設定されます。よろしいですか？
          </>
        )
      })
      return false
    }
    return true
  }

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const requestBody = {
      ...data,
      account_item_id: Number(data.account_item_id)
    }
    if (!fixedAsset) {
      const res = await postFixedAsset(requestBody)
      setCurrentAssetId?.(res.id)
      setIsEditing(index + 1)
    } else {
      await patchFixedAssetBasicInfo(requestBody)
      setIsEditing(-1)
    }

    await mutateFixedAssets()
  }

  const resetForm = () =>
    fixedAsset &&
    reset({
      name: fixedAsset.name,
      acquisition_amount: fixedAsset.acquisition_amount,
      acquisition_date: fixedAsset.acquisition_date,
      quantity: fixedAsset.depreciation?.quantity || 1,
      account_item_id: fixedAsset.account_item.id
    })
  useEffect(() => {
    resetForm()
  }, [fixedAsset])

  return (
    <FormProvider {...methods}>
      {/* 取得日を変更しようとしたときに出すアラートモーダル */}
      {renderConfirmDialog({
        okButton: {
          onClick: () => {
            handleSubmit(onSubmit)()
          }
        },
        cancelButton: {}
      })}
      <FormSection
        editType={fixedAsset ? 'edit' : 'create'}
        index={2}
        title="基本情報"
        id={id}
        // まだ前のフォームを保存していないときは編集できないようにする
        disabled={!done && !isEditing}
      >
        <Collapse in={isEditing || !done}>
          <VStack spacing="20px" align="flex-start">
            <FormTextInput<FormInputs>
              w="180px"
              label="資産の名前"
              placeholder="例）パソコン"
              name="name"
              helpTooltipText="命名のルールは特にありませんが、後から見てどの固定資産を指すのかわかりやすいように入力しましょう。"
              isDisabled={hasPreviousDepreciation}
            />
            <FormYearSelect<FormInputs>
              w="180px"
              label="取得日"
              name="acquisition_date"
              helpTooltipText="固定資産を取得した日を入力しましょう。"
              isDisabled={hasPreviousDepreciation}
              yearProps={{
                start:
                  (processing_year?.processing_year ||
                    new Date().getFullYear()) - 50,
                end: processing_year?.processing_year
              }}
              max={`${
                processing_year?.processing_year || new Date().getFullYear()
              }-12-31`}
            />
            <Box>
              <Flex align="center">
                <FormLabel>
                  金額（購入にかかった費用総額）
                  <QuestionTooltip
                    pos="absolute"
                    right={'-24px'}
                    top="2px"
                    label={
                      '固定資産を取得するためにかかった全ての費用の合計を入力しましょう。（購入手数料・配送料など）'
                    }
                  />
                </FormLabel>
              </Flex>
              <FormPriceAmountInput<FormInputs>
                w="180px"
                name="acquisition_amount"
                isDisabled={hasPreviousDepreciation}
              />
            </Box>
            <FormNumberInput<FormInputs>
              w="180px"
              label="数量"
              name="quantity"
            />
            {accountItems && (
              <FormSelect<FormInputs, number>
                w="180px"
                label="カテゴリ"
                name="account_item_id"
                placeholder="未選択"
                isDisabled={hasPreviousDepreciation}
              >
                {accountItems
                  ?.sort(
                    (a, b) =>
                      accountItemNameToOrderMap(a.name) -
                      accountItemNameToOrderMap(b.name)
                  )
                  .map((accountItem) => (
                    <option key={accountItem.id} value={Number(accountItem.id)}>
                      {accountItem.name}
                    </option>
                  ))}
              </FormSelect>
            )}

            <SaveButtons
              isSubmitting={isSubmitting}
              onSave={() => {
                if (confirmServerUpdateBeforeSubmit()) {
                  handleSubmit(onSubmit)()
                }
              }}
              onCancel={
                done
                  ? () => {
                      setIsEditing(-1)
                      resetForm()
                    }
                  : undefined
              }
            />
          </VStack>
        </Collapse>
        {done &&
          !isEditing &&
          (isOnCreateModal ? (
            <Box>
              <Text
                fontSize="20px"
                fontWeight={700}
                mb="18px"
              >{`${fixedAsset?.name}`}</Text>
              <Button variant="outline" onClick={() => setIsEditing(index)}>
                編集する
              </Button>
            </Box>
          ) : (
            <Box>
              <Flex flexDir="column" gap="24px">
                {[
                  { label: '資産の名前', name: fixedAsset?.name },
                  {
                    label: '取得日',
                    name: fixedAsset?.acquisition_date
                      ? format(
                          new Date(fixedAsset.acquisition_date),
                          'yyyy/MM/dd'
                        )
                      : '-'
                  },
                  {
                    label: '金額（購入にかかった費用総額）',
                    name: `${fixedAsset?.acquisition_amount}円`
                  },
                  {
                    label: '数量',
                    name: fixedAsset?.depreciation?.quantity
                  },
                  {
                    label: 'カテゴリ',
                    name: fixedAsset?.account_item.name
                  }
                ].map((label, i) => (
                  <DataDisplay key={i} {...label} />
                ))}
              </Flex>
              <Button
                mt="28px"
                variant="outline"
                onClick={() => setIsEditing(index)}
              >
                編集する
              </Button>
            </Box>
          ))}
      </FormSection>
    </FormProvider>
  )
}
